import { useCallback, type MutableRefObject } from 'react';
import {
  useLazyGetUserConsentsQuery,
  useUpdateUserConsentMutation,
  type UserConsentItem,
} from '../../../redux/apis/consents.api';

const findAiConsent = (consents?: UserConsentItem[]) =>
  consents?.find((consent) => consent.consentKey?.toUpperCase().includes('AI'));

export type UseAiConsentAgreeOptions = {
  pendingAiConsentRef: MutableRefObject<UserConsentItem | null>;
  closeModal: () => void;
  onAccepted: () => void;
  onError?: (message: string) => void;
};

export type UseAiConsentAgreeResult = {
  handleAiConsentAgree: () => Promise<void>;
  isUpdatingUserConsent: boolean;
};

export const useAiConsentAgree = ({
  pendingAiConsentRef,
  closeModal,
  onAccepted,
  onError,
}: UseAiConsentAgreeOptions): UseAiConsentAgreeResult => {
  const [getUserConsents] = useLazyGetUserConsentsQuery();
  const [updateUserConsent, { isLoading: isUpdatingUserConsent }] = useUpdateUserConsentMutation();

  const handleAiConsentAgree = useCallback(async () => {
    let aiConsent = pendingAiConsentRef.current;

    if (!aiConsent?.consentKey || aiConsent.version == null) {
      try {
        const response = await getUserConsents().unwrap();
        aiConsent = findAiConsent(response?.consents) ?? null;
      } catch (err) {
        console.error('Failed to fetch user consents:', err);
        onError?.('Unable to record consent. Please try again.');
        return;
      }
    }

    if (!aiConsent?.consentKey || aiConsent.version == null) {
      onError?.('Unable to record consent. Please try again.');
      return;
    }

    try {
      const result = await updateUserConsent({
        consentKey: aiConsent.consentKey,
        version: aiConsent.version,
        status: 'ACCEPTED',
      }).unwrap();

      if (result?.status === 'ACCEPTED') {
        closeModal();
        onAccepted();
        return;
      }

      onError?.('Unable to record consent. Please try again.');
    } catch (err) {
      console.error('Failed to update user consent:', err);
      onError?.('Unable to record consent. Please try again.');
    }
  }, [closeModal, getUserConsents, onAccepted, onError, pendingAiConsentRef, updateUserConsent]);

  return {
    handleAiConsentAgree,
    isUpdatingUserConsent,
  };
};
